import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DetailHeader } from '../../components/layout/DetailHeader.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { productionApi } from '../../services/productionApi.js';
import { formatCurrency, formatDateTime, formatNumber } from '../../utils/formatters.js';

import { ConfirmWorkOrderModal } from './ConfirmWorkOrderModal.jsx';

export function WorkOrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Plural key on purpose: the list and the confirmation modal invalidate the
  // ['production', 'work-orders'] prefix, which must cover this page too.
  const wo = useQuery({
    queryKey: ['production', 'work-orders', id],
    queryFn: () => productionApi.getWorkOrder(id).then((r) => r.items),
  });
  const confirmations = useQuery({
    queryKey: ['production', 'wo-confirmations', id],
    queryFn: () => productionApi.listWorkOrderConfirmations(id).then((r) => r.items || []),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['production', 'work-orders'] });
  const onErr = (err) => showToast(err.message || t('common.error'));

  const release = useMutation({ mutationFn: () => productionApi.setWorkOrderStatus(id, 'RELEASED'), onSuccess: invalidate, onError: onErr });
  const start = useMutation({ mutationFn: () => productionApi.setWorkOrderStatus(id, 'IN_PROGRESS'), onSuccess: invalidate, onError: onErr });
  const cancel = useMutation({ mutationFn: () => productionApi.setWorkOrderStatus(id, 'CANCELLED'), onSuccess: invalidate, onError: onErr });
  const complete = useMutation({
    mutationFn: () => productionApi.completeWorkOrder(id),
    onSuccess: () => { invalidate(); showToast(t('production.orders.completedToast')); },
    onError: onErr,
  });

  if (wo.isLoading) {
    return <section className="page"><Skeleton variant="rows" count={6} /></section>;
  }
  if (!wo.data) {
    return <section className="page"><EmptyState message={t('production.orders.empty')} /></section>;
  }

  const w = wo.data;
  const status = w.status;
  const confirmed = Number(w.confirmedQuantity) || 0;
  /* Waehrend COMPLETING wartet der Auftrag auf die Buchung des Lagers. Ein
     zweiter Abschluss oder eine Rueckmeldung dazwischen wuerde die Sache nur
     verwirren - die Knoepfe bleiben deshalb weg. */
  const confirmable = status === 'RELEASED' || status === 'IN_PROGRESS';

  const actions = (
    <>
      {status === 'DRAFT' && <button type="button" className="btn" onClick={() => release.mutate()}>{t('production.orders.release')}</button>}
      {status === 'RELEASED' && <button type="button" className="btn" onClick={() => start.mutate()}>{t('production.orders.start')}</button>}
      {confirmable && (
        <button type="button" className="btn primary" onClick={() => setConfirmOpen(true)}>
          {t('pc2.action.confirm')}
        </button>
      )}
      {/* Once partial confirmations exist the order finishes via the last
          confirmation - the backend rejects a manual complete with 409. */}
      {confirmable && confirmed === 0 && (
        <button type="button" className="btn" onClick={() => complete.mutate()}>{t('production.orders.complete')}</button>
      )}
      {(status === 'DRAFT' || status === 'RELEASED' || status === 'IN_PROGRESS') && (
        <button type="button" className="btn" onClick={() => cancel.mutate()}>{t('production.orders.cancel')}</button>
      )}
    </>
  );

  return (
    <section className="page">
      {/* Ein Abschluss, der nicht gebucht werden konnte, gehoert AN den
          Auftrag - nicht in eine Logdatei. Vorher stand der Auftrag in so
          einem Fall einfach auf "abgeschlossen", und im Lager lag nichts. */}
      {w.stockError && (
        <div className="alert danger">{t('production.orders.stockError', { reason: w.stockError })}</div>
      )}
      {status === 'COMPLETING' && !w.stockError && (
        <div className="alert warning">{t('production.orders.completing')}</div>
      )}

      <DetailHeader
        backTo="/production/work-orders"
        title={w.orderNumber}
        actions={actions}
        metrics={[
          { label: t('common.status'), value: t(`production.status.${status}`) },
          { label: t('production.boms.product'), value: w.productName || w.productSku || '-' },
          {
            label: t('production.orders.quantity'),
            value: confirmed > 0 ? `${formatNumber(confirmed)} / ${formatNumber(w.quantity)}` : formatNumber(w.quantity),
          },
          { label: t('production.orders.materialCost'), value: formatCurrency(w.materialCost) },
          { label: t('production.orders.laborCost'), value: formatCurrency(w.laborCost) },
          { label: t('production.orders.totalCost'), value: formatCurrency(w.totalCost) },
          { label: t('production.orders.unitCost'), value: formatCurrency(w.unitCost) },
        ]}
      />

      <h2>{t('production.orders.componentsTitle')}</h2>
      {(w.components || []).length === 0 ? (
        <EmptyState message={t('production.orders.noComponents')} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('production.boms.component')}</th>
              <th className="num">{t('production.orders.required')}</th>
              <th className="num">{t('production.orders.consumed')}</th>
              <th className="num">{t('production.orders.unitCost')}</th>
            </tr>
          </thead>
          <tbody>
            {w.components.map((c) => (
              <tr key={c.id}>
                <td>{c.componentName || c.componentSku || c.componentProductId}</td>
                <td className="num">{formatNumber(c.requiredQuantity)}</td>
                <td className="num">{formatNumber(c.consumedQuantity)}</td>
                <td className="num">{Number(c.unitCost) > 0 ? formatCurrency(c.unitCost) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(confirmations.data || []).length > 0 && (
        <>
          <h2>{t('pc2.history')}</h2>
          <table className="table">
            <thead>
              <tr>
                <th>{t('pc2.col.number')}</th>
                <th className="num">{t('pc2.quantity')}</th>
                <th className="num">{t('pc2.scrap')}</th>
                <th className="num">{t('pc2.labor')}</th>
                <th>{t('pc2.col.time')}</th>
              </tr>
            </thead>
            <tbody>
              {confirmations.data.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.confirmationNumber}</td>
                  <td className="num mono">{formatNumber(c.confirmedQuantity)}</td>
                  <td className="num mono">{formatNumber(c.scrapQuantity)}</td>
                  <td className="num mono">{formatNumber(c.laborHours)}</td>
                  <td className="muted text-sm">{formatDateTime(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <ConfirmWorkOrderModal workOrder={confirmOpen ? w : null} onClose={() => setConfirmOpen(false)} />
    </section>
  );
}
